import FORM_CONFIG from './formConfig';

/**
 * Configuración para el listado de preguntas
 * Incluye configuración de iconos de estado y debug visual
 * 
 * NUEVA FUNCIONALIDAD: Filtros de preguntas por name, type y grupo
 * 
 * Esta configuración permite ocultar o mostrar preguntas específicas basándose en:
 * - Nombre de la pregunta (name/label)
 * - Tipo de pregunta (text, select_one, etc.)
 * - Nombre del grupo al que pertenece
 * 
 * MODOS DE FILTRADO:
 * - Blacklist (useAsWhitelist: false): Oculta las preguntas que están en las listas
 * - Whitelist (useAsWhitelist: true): Solo muestra las preguntas que están en las listas
 * 
 * EJEMPLOS DE USO:
 * 
 * 1. Ocultar preguntas específicas por nombre:
 *    filters.questionName.list = ['pregunta_admin', 'pregunta_test'];
 * 
 * 2. Mostrar solo preguntas de texto:
 *    filters.filterConfig.useAsWhitelist = true;
 *    filters.questionType.list = ['text', 'note'];
 * 
 * 3. Ocultar todo un grupo:
 *    filters.groupName.list = ['grupo_administrativo'];
 * 
 * 4. Usar coincidencia parcial:
 *    filters.questionName.exactMatch = false;
 *    filters.questionName.list = ['admin']; // Ocultará todas las preguntas que contengan 'admin'
 * 
 * FUNCIONES DISPONIBLES:
 * - shouldFilterQuestion(question, group, filterConfig): Evalúa si una pregunta debe ocultarse
 * - getQuestionFilterDebugInfo(question, group, filterConfig): Obtiene información de debug
 */

/**
 * Configuración de iconos de estado para preguntas
 */
export const QUESTION_STATUS_CONFIG = {
  // Habilitar/deshabilitar iconos de estado (se deshabilita en producción)
  enabled: true,
  
  // Configuración de filtros para mostrar/ocultar iconos
  filters: {
    // Configuración adicional de filtros
    filterConfig: {
      // Si está en true, los filtros actúan como whitelist (solo mostrar los especificados)
      // Si está en false, actúan como blacklist (ocultar los especificados)
      useAsWhitelist: false,
      
      // Habilitar filtros por grupo
      enableGroupFilter: true,
      
      // Habilitar filtros por pregunta individual
      enableQuestionFilter: true,
      
      // Habilitar filtros por tipo
      enableTypeFilter: true
    }
  },
  
  // Configuración de iconos
  icons: {
    // Estilo base del icono
    baseStyle: {
      position: 'absolute',
      top: '8px',
      right: '8px',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      zIndex: 10,
      opacity: 0.8,
      transition: 'opacity 0.2s ease'
    },
    
    // Estilo cuando se hace hover
    hoverStyle: {
      opacity: 1,
      transform: 'scale(1.2)'
    },
    
    // Configuración para múltiples iconos
    multipleIcons: {
      spacing: 12, // Espaciado entre iconos en píxeles
      maxIcons: 3, // Máximo número de iconos a mostrar
      direction: 'horizontal' // 'horizontal' o 'vertical'
    }
  },
  
  // Configuración de colores de estado
  statusColors: {
    // Estado para preguntas padre
    parent: {
      color: '#007bff',
      backgroundColor: '#007bff',
      label: 'Pregunta Padre',
      description: 'Esta pregunta es referenciada por otras preguntas'
    },
    // Estado para preguntas con username en relevant - VISIBLE
    usernameVisible: {
      color: '#28a745',
      backgroundColor: '#28a745',
      label: 'Username - Visible',
      description: 'Esta pregunta tiene ${username} en relevant y se muestra'
    },
    // Estado para preguntas con username en relevant - OCULTA
    usernameHidden: {
      color: '#dc3545',
      backgroundColor: '#dc3545',
      label: 'Username - Oculta',
      description: 'Esta pregunta tiene ${username} en relevant y se oculta'
    },
    // Estado para preguntas con username en relevant - SIN EVALUAR
    usernameNotEvaluated: {
      color: '#ffc107',
      backgroundColor: '#ffc107',
      label: 'Username - No Evaluada',
      description: 'Esta pregunta tiene ${username} en relevant pero no se evalúa'
    }
  },
  
  // Configuración de tooltips
  tooltips: {
    enabled: true,
    showDelay: 500,
    hideDelay: 200,
    maxWidth: '250px',
    backgroundColor: '#333',
    color: '#fff',
    fontSize: '12px',
    padding: '8px 12px',
    borderRadius: '4px',
    zIndex: 1000
  },
  
  // Configuración de debug
  debug: {
    enabled: true,
    showInConsole: true,
    showStatusInfo: true
  }
};

/**
 * Función para evaluar si una pregunta debe ser filtrada (ocultada)
 * @param {Object} question - La pregunta a evaluar
 * @param {Object} group - El grupo al que pertenece la pregunta (opcional)
 * @param {Object} filterConfig - Configuración de filtros (por defecto usa LISTADO_PREGUNTAS_CONFIG.filters)
 * @returns {boolean} - true si la pregunta debe ser ocultada, false si debe mostrarse
 */
export const shouldFilterQuestion = (question, group = null, filterConfig = null) => {
  // Usar configuración por defecto si no se proporciona
  const config = filterConfig || LISTADO_PREGUNTAS_CONFIG.filters;
  
  // Si no hay configuración o la pregunta no existe, no filtrar
  if (!config || !question) {
    return false;
  }
  
  // Política global: ocultar preguntas bloqueadas por admin (config_actualizable = 0/false)
  try {
    const hideNotUpdatable = FORM_CONFIG?.visibilityPolicies?.hideNotUpdatable?.enabled === true;
    const isNotUpdatable = (question.config_actualizable === false || question.config_actualizable === 0 || question.config_actualizable === '0');
    if (hideNotUpdatable && isNotUpdatable) {
      return true;
    }
  } catch (e) {
    // Ignorar errores de import/config
  }
  
  const { filterConfig: generalConfig } = config;
  const useAsWhitelist = generalConfig?.useAsWhitelist || false;
  
  // Array para almacenar los resultados de cada filtro
  const filterResults = [];
  
  // Función helper para comparar strings
  const compareStrings = (str1, str2, exactMatch = true, caseSensitive = false) => {
    if (!str1 || !str2) return false;
    
    const s1 = caseSensitive ? str1 : str1.toLowerCase();
    const s2 = caseSensitive ? str2 : str2.toLowerCase();
    
    return exactMatch ? s1 === s2 : s1.includes(s2) || s2.includes(s1);
  };
  
  // Filtro por nombre de pregunta
  if (config.questionName?.enabled && generalConfig?.enableQuestionFilter) {
    const nameConfig = config.questionName;
    const questionName = question.name || question.label || '';
    
    if (nameConfig.list && nameConfig.list.length > 0) {
      const isInList = nameConfig.list.some(name => 
        compareStrings(questionName, name, nameConfig.exactMatch, nameConfig.caseSensitive)
      );
      filterResults.push({ type: 'name', isInList });
    }
  }
  
  // Filtro por tipo de pregunta
  if (config.questionType?.enabled && generalConfig?.enableTypeFilter) {
    const typeConfig = config.questionType;
    const questionType = question.type || '';
    
    if (typeConfig.list && typeConfig.list.length > 0) {
      const isInList = typeConfig.list.some(type => 
        compareStrings(questionType, type, typeConfig.exactMatch, typeConfig.caseSensitive)
      );
      filterResults.push({ type: 'type', isInList });
    }
  }
  
  // Filtro por nombre del grupo
  if (config.groupName?.enabled && generalConfig?.enableGroupFilter && group) {
    const groupConfig = config.groupName;
    const groupName = group.name || group.label || '';
    const groupPath = group.path || '';
    
    if (groupConfig.list && groupConfig.list.length > 0) {
      let isInList = false;
      
      // Verificar nombre del grupo
      isInList = groupConfig.list.some(name => 
        compareStrings(groupName, name, groupConfig.exactMatch, groupConfig.caseSensitive)
      );
      
      // Si includeGroupPath está habilitado, también verificar el path
      if (!isInList && groupConfig.includeGroupPath && groupPath) {
        isInList = groupConfig.list.some(name => 
          compareStrings(groupPath, name, groupConfig.exactMatch, groupConfig.caseSensitive)
        );
      }
      
      filterResults.push({ type: 'group', isInList });
    }
  }
  
  // Si no hay filtros aplicables, no filtrar
  if (filterResults.length === 0) {
    return false;
  }
  
  // Evaluar resultados según el modo (whitelist o blacklist)
  if (useAsWhitelist) {
    // Modo whitelist: ocultar si NO está en ninguna lista
    return !filterResults.some(result => result.isInList);
  } else {
    // Modo blacklist: ocultar si está en alguna lista
    return filterResults.some(result => result.isInList);
  }
};

/**
 * Función para obtener información de debug sobre el filtrado de una pregunta
 * @param {Object} question - La pregunta a evaluar
 * @param {Object} group - El grupo al que pertenece la pregunta (opcional)
 * @param {Object} filterConfig - Configuración de filtros (por defecto usa LISTADO_PREGUNTAS_CONFIG.filters)
 * @returns {Object} - Información de debug sobre el filtrado
 */
export const getQuestionFilterDebugInfo = (question, group = null, filterConfig = null) => {
  const config = filterConfig || LISTADO_PREGUNTAS_CONFIG.filters;
  
  if (!config || !question) {
    return { filtered: false, reason: 'No config or question' };
  }
  
  const shouldFilter = shouldFilterQuestion(question, group, filterConfig);
  const { filterConfig: generalConfig } = config;
  const useAsWhitelist = generalConfig?.useAsWhitelist || false;
  
  const debugInfo = {
    filtered: shouldFilter,
    mode: useAsWhitelist ? 'whitelist' : 'blacklist',
    questionName: question.name || question.label || '',
    questionType: question.type || '',
    groupName: group?.name || group?.label || '',
    groupPath: group?.path || '',
    appliedFilters: [],
    reason: ''
  };
  
  // Verificar cada filtro
  if (config.questionName?.enabled && generalConfig?.enableQuestionFilter) {
    const nameConfig = config.questionName;
    if (nameConfig.list && nameConfig.list.length > 0) {
      const isInList = nameConfig.list.some(name => {
        const s1 = nameConfig.caseSensitive ? debugInfo.questionName : debugInfo.questionName.toLowerCase();
        const s2 = nameConfig.caseSensitive ? name : name.toLowerCase();
        return nameConfig.exactMatch ? s1 === s2 : s1.includes(s2) || s2.includes(s1);
      });
      debugInfo.appliedFilters.push({
        type: 'name',
        isInList,
        config: nameConfig
      });
    }
  }
  
  if (config.questionType?.enabled && generalConfig?.enableTypeFilter) {
    const typeConfig = config.questionType;
    if (typeConfig.list && typeConfig.list.length > 0) {
      const isInList = typeConfig.list.some(type => {
        const s1 = typeConfig.caseSensitive ? debugInfo.questionType : debugInfo.questionType.toLowerCase();
        const s2 = typeConfig.caseSensitive ? type : type.toLowerCase();
        return typeConfig.exactMatch ? s1 === s2 : s1.includes(s2) || s2.includes(s1);
      });
      debugInfo.appliedFilters.push({
        type: 'type',
        isInList,
        config: typeConfig
      });
    }
  }
  
  if (config.groupName?.enabled && generalConfig?.enableGroupFilter && group) {
    const groupConfig = config.groupName;
    if (groupConfig.list && groupConfig.list.length > 0) {
      let isInList = false;
      
      // Verificar nombre del grupo
      isInList = groupConfig.list.some(name => {
        const s1 = groupConfig.caseSensitive ? debugInfo.groupName : debugInfo.groupName.toLowerCase();
        const s2 = groupConfig.caseSensitive ? name : name.toLowerCase();
        return groupConfig.exactMatch ? s1 === s2 : s1.includes(s2) || s2.includes(s1);
      });
      
      // Si includeGroupPath está habilitado, también verificar el path
      if (!isInList && groupConfig.includeGroupPath && debugInfo.groupPath) {
        isInList = groupConfig.list.some(name => {
          const s1 = groupConfig.caseSensitive ? debugInfo.groupPath : debugInfo.groupPath.toLowerCase();
          const s2 = groupConfig.caseSensitive ? name : name.toLowerCase();
          return groupConfig.exactMatch ? s1 === s2 : s1.includes(s2) || s2.includes(s1);
        });
      }
      
      debugInfo.appliedFilters.push({
        type: 'group',
        isInList,
        config: groupConfig
      });
    }
  }
  
  // Generar razón del filtrado
  if (debugInfo.appliedFilters.length === 0) {
    debugInfo.reason = 'No filters applied';
  } else if (useAsWhitelist) {
    const inAnyList = debugInfo.appliedFilters.some(f => f.isInList);
    debugInfo.reason = inAnyList ? 'In whitelist - shown' : 'Not in whitelist - hidden';
  } else {
    const inAnyList = debugInfo.appliedFilters.some(f => f.isInList);
    debugInfo.reason = inAnyList ? 'In blacklist - hidden' : 'Not in blacklist - shown';
  }
  
  return debugInfo;
};

/**
 * Función para determinar el estado de una pregunta
 * @param {Object} question - La pregunta a evaluar
 * @param {Array} allQuestions - Todas las preguntas del formulario
 * @param {Object} formValues - Valores actuales del formulario
 * @returns {Object|null} - Objeto con información del estado o null si no hay estado
 */
export const determineQuestionStatus = (question, allQuestions = [], formValues = {}) => {
  if (!QUESTION_STATUS_CONFIG.enabled || !question) {
    return null;
  }

  const statuses = [];

  // Verificar si es una pregunta padre (referenciada por otras preguntas)
  const isParent = allQuestions.some(q => 
    q.id !== question.id && 
    q.dependencies && 
    q.dependencies.includes(question.id)
  );

  if (isParent) {
    statuses.push({
      type: 'parent',
      ...QUESTION_STATUS_CONFIG.statusColors.parent,
      priority: 1
    });
  }

  // Verificar si tiene ${username} en relevant (soporte para relevant_transformed y legacy)
  const containsUsernameInRelevantTransformed = (rel) => {
    if (!rel) return false;
    if (Array.isArray(rel)) {
      return rel.some(containsUsernameInRelevantTransformed);
    }
    if (typeof rel === 'object') {
      if (rel.type === 'function' && rel.name === 'selected') {
        return rel.field === 'username' || (typeof rel.value === 'string' && rel.value.includes('username'));
      }
      if (rel.type === 'comparison') {
        if (rel.field === 'username') return true;
        return typeof rel.value === 'string' && rel.value.includes('${username}');
      }
      if (rel.type === 'logical' && Array.isArray(rel.expressions)) {
        return rel.expressions.some(containsUsernameInRelevantTransformed);
      }
      if (rel.field === 'username') return true;
      return Object.values(rel).some(v => typeof v === 'string' && v.includes('${username}'));
    }
    if (typeof rel === 'string') {
      return rel.includes('${username}');
    }
    return false;
  };
  const hasUsernameInRelevant = containsUsernameInRelevantTransformed(question.relevant_transformed) || (typeof question.relevant === 'string' && question.relevant.includes('${username}'));
  
  if (hasUsernameInRelevant) {
    // Determinar el estado basado en la evaluación y visibilidad
    let usernameStatus = 'usernameNotEvaluated'; // Por defecto no evaluada
    
    // Si la pregunta tiene la propiedad mostrar_pregunta (viene del RelevantManager)
    if (question.hasOwnProperty('mostrar_pregunta')) {
      usernameStatus = question.mostrar_pregunta ? 'usernameVisible' : 'usernameHidden';
    }
    // Si no tiene mostrar_pregunta, verificar si está en el DOM (visible)
    else if (question.visible !== undefined) {
      usernameStatus = question.visible ? 'usernameVisible' : 'usernameHidden';
    }
    // Fallback: verificar configuración global
    else {
      // Importar configuración si está disponible
      try {
        const FORM_CONFIG = require('./formConfig').FORM_CONFIG;
      const usernameEvaluationEnabled = FORM_CONFIG?.relevantLogic?.usernameConfig?.evaluate ?? 
                                           FORM_CONFIG?.relevantLogic?.enableUsernameEvaluation ?? false;
      const defaultVisibility = FORM_CONFIG?.relevantLogic?.usernameConfig?.defaultVisibility ?? true;
      
      if (usernameEvaluationEnabled) {
        // Si está habilitada la evaluación, necesitaríamos evaluar la expresión
        // Por ahora, usar defaultVisibility como fallback
        usernameStatus = defaultVisibility ? 'usernameVisible' : 'usernameHidden';
      } else {
        usernameStatus = 'usernameNotEvaluated';
      }
      } catch (error) {
        // Si no se puede importar la configuración, usar estado no evaluado
        usernameStatus = 'usernameNotEvaluated';
      }
    }

    statuses.push({
      type: usernameStatus,
      ...QUESTION_STATUS_CONFIG.statusColors[usernameStatus],
      priority: 2
    });
  }

  // Si no hay estados, retornar null
  if (statuses.length === 0) {
    return null;
  }

  // Ordenar por prioridad y limitar al máximo configurado
  const sortedStatuses = statuses
    .sort((a, b) => a.priority - b.priority)
    .slice(0, QUESTION_STATUS_CONFIG.icons.multipleIcons.maxIcons);

  // Retornar objeto con todos los estados aplicables
  return {
    statuses: sortedStatuses,
    hasMultipleStatuses: sortedStatuses.length > 1,
    primaryStatus: sortedStatuses[0], // Para compatibilidad hacia atrás
    type: sortedStatuses[0].type // Para compatibilidad hacia atrás
  };
};

/**
 * Función para generar el tooltip de una pregunta
 * @param {Object} questionStatusResult - Resultado del estado de la pregunta (con statuses array)
 * @param {Object} question - La pregunta
 * @returns {string} Contenido del tooltip
 */
export const generateQuestionTooltip = (questionStatusResult, question) => {
  if (!questionStatusResult || !QUESTION_STATUS_CONFIG.tooltips.enabled) {
    return '';
  }
  
  const { statuses, hasMultipleStatuses } = questionStatusResult;
  
  if (!statuses || statuses.length === 0) {
    return '';
  }
  
  let tooltip = `<strong>Pregunta:</strong> ${question.name || question.label || 'Sin nombre'}<br/><br/>`;
  
  if (!hasMultipleStatuses) {
    // Un solo estado
    const status = statuses[0];
    tooltip += `<strong>${status.label}</strong><br/>`;
    tooltip += `${status.description}`;
  } else {
    // Múltiples estados
    tooltip += `<strong>Estados múltiples:</strong><br/>`;
    statuses.forEach((status, index) => {
      const config = QUESTION_STATUS_CONFIG.statusColors[status.type];
      if (config) {
        tooltip += `<span style="color: ${config.color};">●</span> <strong>${config.label}</strong><br/>`;
        tooltip += `&nbsp;&nbsp;&nbsp;${config.description}`;
        if (index < statuses.length - 1) {
          tooltip += '<br/><br/>';
        }
      }
    });
  }
  
  // Información adicional de debug si está habilitada
  if (QUESTION_STATUS_CONFIG.debug.enabled && QUESTION_STATUS_CONFIG.debug.showStatusInfo) {
    tooltip += '<br/><br/><small><em>Debug Info:</em><br/>';
    tooltip += `Estados encontrados: ${statuses.length}<br/>`;
    tooltip += `Tipos: ${statuses.map(s => s.type).join(', ')}</small>`;
  }
  
  return tooltip;
};

/**
 * Configuración general del listado de preguntas
 */
export const LISTADO_PREGUNTAS_CONFIG = {
  // Configuración de iconos de estado
  statusIcons: QUESTION_STATUS_CONFIG,
  
  // Configuración específica para filtrado por username
  usernameFiltering: {
    enabled: true, // Habilitar filtrado por username (deshabilitado en producción)
    debugMode: true, // Mostrar información de debug para username
    showBadges: true, // Mostrar badges de estado para preguntas con username
    applyFiltering: false, // Aplicar filtrado real (ocultar preguntas)
    
    // Configuración de evaluación
    evaluation: {
      enabled: false, // Habilitar evaluación de expresiones con ${username}
      defaultVisibility: true, // Visibilidad por defecto cuando no se evalúa
      targetUsername: '' // Username objetivo para filtrado (vacío - no se usa para pruebas)
    },
    
    // Configuración de badges
    badges: {
      colors: {
        visible: '#28a745',
        hidden: '#dc3545',
        notEvaluated: '#ffc107',
        default: '#6c757d'
      },
      text: {
        visible: 'USERNAME ✓',
        hidden: 'USERNAME ✗',
        notEvaluated: 'USERNAME ?'
      }
    },
    
    // Configuración de debug visual
    debugVisual: {
      showRelevantExpression: true, // Mostrar la expresión relevant completa
      showEvaluationResult: true, // Mostrar el resultado de la evaluación
      highlightUsernameQuestions: true, // Resaltar preguntas con username
      highlightColor: 'rgba(255, 193, 7, 0.2)' // Color de resaltado
    }
  },
  
  // Configuración de relevancia para preguntas (similar al menú)
  relevantFiltering: {
    applyRelevantFilters: true,
    showEvaluationColors: false,
    showDebugConditionBadge: false,
    showDebugValues: true,
    showRawRelevantExpression: true,
    maxExpressionLength: 1000
  },
  
  // Otras configuraciones del listado
  display: {
    showQuestionNumbers: true,
    showQuestionTypes: true,
    showRequiredIndicator: true,
    compactMode: false
  },
  
  // Configuración de filtros para mostrar/ocultar preguntas completas
  filters: {
    // Configuración adicional de filtros
    filterConfig: {
      // Si está en true, los filtros actúan como whitelist (solo mostrar los especificados)
      // Si está en false, actúan como blacklist (ocultar los especificados)
      useAsWhitelist: false,
      
      // Habilitar filtros por grupo
      enableGroupFilter: true,
      
      // Habilitar filtros por pregunta individual
      enableQuestionFilter: true,
      
      // Habilitar filtros por tipo
      enableTypeFilter: true
    },
    
    // Filtros por nombre de pregunta
    questionName: {
      // Habilitar filtrado por nombre de pregunta
      enabled: true,
      
      // Lista de nombres de preguntas a ocultar (cuando useAsWhitelist = false)
      // o lista de nombres de preguntas a mostrar (cuando useAsWhitelist = true)
      list: [
        // Ejemplos:
        // 'nombre_pregunta_1',
        // 'nombre_pregunta_2',
        // 'pregunta_especifica'
      ],
      
      // Usar coincidencia exacta o parcial
      exactMatch: true,
      
      // Usar case sensitive o insensitive
      caseSensitive: false
    },
    
    // Filtros por tipo de pregunta
    questionType: {
      // Habilitar filtrado por tipo de pregunta
      enabled: true,
      
      // Lista de tipos de preguntas a ocultar (cuando useAsWhitelist = false)
      // o lista de tipos de preguntas a mostrar (cuando useAsWhitelist = true)
      list: [
        // Ejemplos de tipos comunes:
        'hidden',
        'default',
        'note',
        'calculate'
      ],
      
      // Usar coincidencia exacta o parcial
      exactMatch: true,
      
      // Usar case sensitive o insensitive
      caseSensitive: false
    }
  },

  // Configuración de interacción
  interaction: {
    enableHover: true,
    enableSelection: true,
    enableTooltips: true
  }
};

// Exportar configuración por defecto
export default LISTADO_PREGUNTAS_CONFIG;